import { type Course, UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { useRouter } from "next/compat/router";
import posthog from "posthog-js";
import { useMemo } from "react";
import toast from "react-hot-toast";
import { FaLock } from "react-icons/fa";

import CourseExamPortalModalAttempt from "./CourseExamPortalModalAttempt";
import CourseInfoPreview from "./CourseInfoPreview";
import Button from "~/components/Button";
import Dialog from "~/components/Dialog";
import { useFinalExamAttempts, usePassedCourses, useUserAction } from "~/hooks";
import useLatestExamAttemptSummary from "~/hooks/useLatestExamAttemptSummary";
import usePracticeExamAttempts from "~/hooks/usePracticeExamAttempts";
import useTrackCTA from "~/hooks/useTrackCTA";
import ExamReviewModal from "~/pages/courses/[slug]/exams/components/ExamReviewModal";
import { formatExamUnlockDistance } from "~/utils/formatExamUnlockDistance";

type Props = {
	isOpen: boolean;
	onClose: () => void;
	course: Course;
	hasCourseAccess: boolean;
};

const CourseExamPortalModal = ({
	isOpen,
	onClose,
	course,
	hasCourseAccess,
}: Props) => {
	const { pastAttempts } = usePracticeExamAttempts();
	const { data: finalExamAttempts } = useFinalExamAttempts();
	const { isCoursePassed } = usePassedCourses();
	const router = useRouter();

	const { isSignedIn } = useAuth();
	const trackCTA = useTrackCTA();
	const { setUserAction } = useUserAction();

	const { data: summary } = useLatestExamAttemptSummary(course.slug);

	const unlockDistance = formatExamUnlockDistance(
		finalExamAttempts?.unlockDate,
		{
			addSuffix: false,
			includeSeconds: false,
		},
	);

	const hasOnGoingAttempt = useMemo(
		() =>
			finalExamAttempts?.pastAttempts.find(
				(attempt) =>
					attempt.isPassed === null &&
					attempt.expiresAt &&
					new Date(attempt.expiresAt) > new Date(),
			),
		[finalExamAttempts?.pastAttempts],
	);

	const hasAttempts = useMemo(
		() =>
			(pastAttempts?.length || finalExamAttempts?.pastAttempts.length || 0) > 0,
		[finalExamAttempts?.pastAttempts.length, pastAttempts?.length],
	);

	return (
		<Dialog
			isOpen={isOpen}
			onClose={onClose}
			className="max-w-xl phablet:pb-8"
			title="Coaching and Exams"
		>
			<p className="-mt-1">
				The passing score for the exam is 70%, You can attempt the final exam a
				maximum of two times within a 30-day period. You can take the practice
				exam as many times as you like.
			</p>

			<h3 className="font-semibold text-xl">Coaching</h3>
			{hasAttempts && summary ? (
				<ExamReviewModal
					courseSlug={course.slug}
					trigger={(openModal) => (
						<Button
							size="lg"
							variant="coach"
							onClick={() => {
								posthog.capture("exam_coach:course_button_clicked");
								openModal();
							}}
						>
							Latest Exam Report and Coaching
						</Button>
					)}
				/>
			) : hasAttempts && !summary ? (
				<div className="-m-1 rounded-lg bg-orange-50 p-3 dark:bg-orange-900">
					<blockquote className="mx-auto text-orange-800 dark:text-orange-200">
						Take the practice exam to receive personalized coaching
						recommendations!
					</blockquote>
				</div>
			) : (
				<div className="-m-1 rounded-lg bg-orange-50 p-3 dark:bg-orange-900">
					<blockquote className="mx-auto text-orange-800 dark:text-orange-200">
						You have not taken the practice exam for this course. Take the
						practice exam to receive personalized coaching recommendations!
					</blockquote>
				</div>
			)}

			<h3 className="font-semibold text-xl">Exams</h3>
			{!isCoursePassed(course._id) && finalExamAttempts && (
				<div className="-mt-1 mb-1">
					{(finalExamAttempts.remainingAttempts ?? 0) > 0 ? (
						<blockquote className="text-sky-800 dark:text-sky-200">
							{finalExamAttempts.remainingAttempts} final exam attempt
							{finalExamAttempts.remainingAttempts === 1 ? "" : "s"} remaining.
						</blockquote>
					) : (
						<blockquote className="text-orange-800 dark:text-orange-200">
							No attempts remaining for the final exam
							{unlockDistance
								? `! You can retake the exam in ${unlockDistance}`
								: ""}
							.
						</blockquote>
					)}
				</div>
			)}

			<CourseInfoPreview course={course} />

			<div className="mt-4 flex flex-1 gap-4">
				<Button
					variant="outlined"
					size="lg"
					id={"practice-exam-btn"}
					className="!py-2 w-full bg-white font-medium phablet:text-[1.05rem] hover:bg-white hover:brightness-95 dark:bg-slate-800"
					onClick={async () => {
						if (!isSignedIn) {
							trackCTA(UserAction.SIGN_UP, "Course Practice Exam");

							setUserAction(UserAction.SIGN_UP);
						} else if (!hasCourseAccess) {
							trackCTA(UserAction.BECOME_A_MEMBER, "Course Practice Exam");

							setUserAction(UserAction.BECOME_A_MEMBER);
						} else {
							router?.push(`/courses/${course.slug}/exams/practice`);
						}
					}}
				>
					Practice Exam
				</Button>

				<Button
					size={"lg"}
					id={"final-exam-btn"}
					className="!py-2 w-full font-medium phablet:text-[1.05rem]"
					onClick={async () => {
						if (!isSignedIn) {
							trackCTA(UserAction.SIGN_UP, "Course Final Exam");

							setUserAction(UserAction.SIGN_UP);
						} else if (!hasCourseAccess) {
							trackCTA(UserAction.BECOME_A_MEMBER, "Course Final Exam");

							setUserAction(UserAction.BECOME_A_MEMBER);
						} else if (isCoursePassed(course._id)) {
							toast.error(
								"You have already passed the final exam for this course.",
							);
						} else if (
							finalExamAttempts?.remainingAttempts === 0 &&
							!hasOnGoingAttempt
						) {
							toast.error(
								unlockDistance
									? `No attempts remaining! You can retake the exam in ${unlockDistance}`
									: "No attempts remaining for the final exam.",
							);
						} else {
							router?.push(`/courses/${course.slug}/exams/final`);
						}
					}}
				>
					{hasOnGoingAttempt ? (
						"Continue Exam"
					) : (
						<>
							Final Exam{" "}
							{finalExamAttempts?.remainingAttempts === 0 &&
								isCoursePassed(course._id) && (
									<FaLock className="ml-2" size={16} />
								)}
						</>
					)}
				</Button>
			</div>

			{hasAttempts && <h3 className="font-semibold text-xl">Exam History</h3>}

			{!!finalExamAttempts?.pastAttempts.length && (
				<div className="flex flex-col gap-4">
					<h3 className="font-semibold text-lg">Final Exam</h3>

					<div className="overflow-auto rounded-xl border">
						<table className="w-full border-collapse">
							<thead className="border-b">
								<tr>
									<th className="px-3 py-1 text-left font-semibold">Date</th>

									<th className="px-3 py-1 text-left font-semibold">Status</th>

									<th className="px-3 py-1 text-left font-semibold">Score</th>
								</tr>
							</thead>

							<tbody>
								{finalExamAttempts.pastAttempts?.map((attempt) => (
									<CourseExamPortalModalAttempt
										key={attempt.id}
										attempt={attempt}
									/>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{!!pastAttempts?.length && (
				<div className="flex flex-col gap-4">
					<h3 className="font-semibold text-lg">Practice Exam</h3>
					<div className="overflow-auto rounded-xl border">
						<table className="w-full border-collapse">
							<thead className="border-b">
								<tr>
									<th className="px-3 py-1 text-left font-semibold">Date</th>

									<th className="px-3 py-1 text-left font-semibold">Status</th>

									<th className="px-3 py-1 text-left font-semibold">Score</th>
								</tr>
							</thead>

							<tbody>
								{pastAttempts?.map((attempt) => (
									<CourseExamPortalModalAttempt
										key={attempt.id}
										attempt={attempt}
									/>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}
		</Dialog>
	);
};

export default CourseExamPortalModal;
